import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrganisations, suspendOrg, reactivateOrg } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import TCLogo from '../auth/TCLogo';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logoutUser } = useAuth();

  React.useEffect(() => {
    if (!user || user.role !== 'platform_admin') {
      navigate('/admin');
    }
  }, [user, navigate]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewOrg, setShowNewOrg] = useState(false);
  const [form, setForm] = useState({
    name: '', slug: '', plan: 'standard',
    super_admin_name: '', super_admin_email: '', super_admin_password: ''
  });
  const [creating, setCreating] = useState(false);

  const loadOrgs = async () => {
    try {
      const res = await getOrganisations();
      setOrgs(res.data);
    } catch (err) {
      setError('Failed to load organisations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrgs(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setCreating(true);
    try {
      const api = (await import('../../services/api')).default;
      await api.post('/platform/organisations', form);
      setSuccess(`Organisation ${form.name} created successfully`);
      setShowNewOrg(false);
      setForm({ name: '', slug: '', plan: 'standard', super_admin_name: '', super_admin_email: '', super_admin_password: '' });
      loadOrgs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create organisation');
    } finally {
      setCreating(false);
    }
  };

  const handleSuspend = async (id, name) => {
    if (!window.confirm(`Suspend ${name}?`)) return;
    try {
      await suspendOrg(id);
      setSuccess(`${name} suspended`);
      loadOrgs();
    } catch (err) {
      setError('Failed to suspend');
    }
  };

  const handleReactivate = async (id, name) => {
    try {
      await reactivateOrg(id);
      setSuccess(`${name} reactivated`);
      loadOrgs();
    } catch (err) {
      setError('Failed to reactivate');
    }
  };

  const handleLogout = () => {
    logoutUser();
    navigate('/admin');
  };

  return (
    <div style={{minHeight:'100vh',background:'#f5f7fa'}}>
      {/* Top Nav */}
      <div style={{background:'linear-gradient(135deg, #1B3A5C, #7D6608)',padding:'0 24px',display:'flex',alignItems:'center',justifyContent:'space-between',height:'60px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          <TCLogo size={36} />
          <div>
            <div style={{color:'white',fontWeight:'700',fontSize:'16px'}}>CONSAM Platform</div>
            <div style={{color:'rgba(255,255,255,0.6)',fontSize:'11px'}}>Administration Console</div>
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'16px'}}>
          <span style={{color:'rgba(255,255,255,0.8)',fontSize:'13px'}}>{user?.name}</span>
          <button onClick={handleLogout}
            style={{background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',color:'white',padding:'6px 14px',borderRadius:'6px',cursor:'pointer',fontSize:'13px'}}>
            Sign Out
          </button>
        </div>
      </div>

      <div style={{padding:'24px'}}>
        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'24px'}}>
          <div>
            <h1 style={{fontSize:'24px',fontWeight:'700',color:'#1B3A5C'}}>Organisations</h1>
            <p style={{color:'#595959',fontSize:'14px',marginTop:'4px'}}>
              {orgs.length} organisation{orgs.length !== 1 ? 's' : ''} on the platform
            </p>
          </div>
          <button className="btn btn-primary"
            onClick={() => { setShowNewOrg(!showNewOrg); setError(''); }}
            style={{background:'#7D6608'}}>
            {showNewOrg ? 'Cancel' : '+ New Organisation'}
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div style={{background:'#FCE4D6',color:'#C0392B',padding:'10px 16px',borderRadius:'6px',marginBottom:'16px',display:'flex',justifyContent:'space-between'}}>
            {error}<button onClick={() => setError('')} style={{background:'none',border:'none',cursor:'pointer'}}>✕</button>
          </div>
        )}
        {success && (
          <div style={{background:'#E2F0D9',color:'#1E7B34',padding:'10px 16px',borderRadius:'6px',marginBottom:'16px',display:'flex',justifyContent:'space-between'}}>
            {success}<button onClick={() => setSuccess('')} style={{background:'none',border:'none',cursor:'pointer'}}>✕</button>
          </div>
        )}

        {/* New Org Form */}
        {showNewOrg && (
          <div style={{background:'white',borderRadius:'8px',padding:'24px',marginBottom:'24px',boxShadow:'0 1px 3px rgba(0,0,0,0.1)'}}>
            <h3 style={{fontSize:'16px',fontWeight:'600',color:'#1B3A5C',marginBottom:'16px'}}>Create New Organisation</h3>
            <form onSubmit={handleCreate}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))',gap:'16px'}}>
                <div className="form-group">
                  <label>Organisation Name *</label>
                  <input type="text" value={form.name}
                    onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="e.g. ACME Industries" required />
                </div>
                <div className="form-group">
                  <label>URL Slug * (lowercase, no spaces)</label>
                  <input type="text" value={form.slug}
                    onChange={e => setForm({...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'')})}
                    placeholder="e.g. acme" required />
                  {form.slug && (
                    <p style={{fontSize:'11px',color:'#2E75B6',marginTop:'4px'}}>
                      URL: tathya.consamerp.com/{form.slug}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label>Plan</label>
                  <select value={form.plan} onChange={e => setForm({...form, plan: e.target.value})}>
                    <option value="standard">Standard</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Super Admin Name *</label>
                  <input type="text" value={form.super_admin_name}
                    onChange={e => setForm({...form, super_admin_name: e.target.value})}
                    placeholder="Full name" required />
                </div>
                <div className="form-group">
                  <label>Super Admin Email *</label>
                  <input type="email" value={form.super_admin_email}
                    onChange={e => setForm({...form, super_admin_email: e.target.value})}
                    placeholder="admin@company.com" required />
                </div>
                <div className="form-group">
                  <label>Temporary Password *</label>
                  <input type="password" value={form.super_admin_password}
                    onChange={e => setForm({...form, super_admin_password: e.target.value})}
                    placeholder="Min 8 characters" required />
                </div>
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',gap:'12px',marginTop:'16px',paddingTop:'16px',borderTop:'1px solid #f0f0f0'}}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewOrg(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{background:'#7D6608'}} disabled={creating}>
                  {creating ? 'Creating...' : 'Create Organisation'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Organisations Table */}
        <div style={{background:'white',borderRadius:'8px',boxShadow:'0 1px 3px rgba(0,0,0,0.1)'}}>
          {loading ? (
            <div style={{padding:'40px',textAlign:'center',color:'#2E75B6'}}>Loading...</div>
          ) : orgs.length === 0 ? (
            <div style={{padding:'60px',textAlign:'center',color:'#595959'}}>
              No organisations yet. Create the first one above.
            </div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'14px'}}>
              <thead>
                <tr>
                  {['#','Organisation','Slug','Plan','Status','Created','Actions'].map(h => (
                    <th key={h} style={{padding:'10px 12px',background:'#1B3A5C',color:'white',fontWeight:'600',textAlign:'left',fontSize:'13px'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orgs.map((org, idx) => (
                  <tr key={org.id} style={{borderBottom:'1px solid #f0f0f0'}}>
                    <td style={{padding:'12px',color:'#595959',fontSize:'12px'}}>{idx+1}</td>
                    <td style={{padding:'12px',fontWeight:'600',color:'#1B3A5C'}}>{org.name}</td>
                    <td style={{padding:'12px'}}>
                      <a href={`https://tathya.consamerp.com/${org.slug}`}
                        target="_blank" rel="noreferrer"
                        style={{color:'#2E75B6',fontSize:'12px'}}>
                        /{org.slug}
                      </a>
                    </td>
                    <td style={{padding:'12px'}}>
                      <span style={{background:'#FEF9E7',color:'#7D6608',padding:'3px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600'}}>
                        {org.plan}
                      </span>
                    </td>
                    <td style={{padding:'12px'}}>
                      <span style={{background: org.status==='active' ? '#E2F0D9' : '#FCE4D6',color: org.status==='active' ? '#1E7B34' : '#C0392B',padding:'3px 8px',borderRadius:'10px',fontSize:'11px',fontWeight:'600'}}>
                        {org.status}
                      </span>
                    </td>
                    <td style={{padding:'12px',fontSize:'12px',color:'#595959'}}>
                      {org.created_at ? new Date(org.created_at).toLocaleDateString() : ''}
                    </td>
                    <td style={{padding:'12px'}}>
                      {org.status === 'active' ? (
                        <button className="btn btn-danger"
                          style={{padding:'4px 10px',fontSize:'12px'}}
                          onClick={() => handleSuspend(org.id, org.name)}>
                          Suspend
                        </button>
                      ) : (
                        <button className="btn btn-primary"
                          style={{padding:'4px 10px',fontSize:'12px'}}
                          onClick={() => handleReactivate(org.id, org.name)}>
                          Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
